import { ChangeEvent, FormEvent, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { twMerge } from "tailwind-merge";
import Editor from "components/Editor";
import ConsoleHrozontalHeadTable from "../-components/ConsolehorizontalHeadTable";
import { allowedMimeTypes, toastErrorSyle } from "hooks/utils/util";
import { usePostConsoleNotice } from "hooks/console/usePostConsoleNotice";
import { usePutConsoleNotice } from "hooks/console/usePutConsoleNotice";
import LoadingPop from "components/popup/LoadingPop";

export const theadStyle = "text-[#222222] text-[16px] text-center";
const excludedKeys = ["notice", "b_file", "list_no", "contents"]; // 제외할 키 목록

export type NoticeFormProps = {
    category: string;
    name: string;
    subject: string;
    contents: string;
    notice: number;
    b_file: string;
    list_no: number | null;
};

export default function NoticesForm({
    type,
    form,
    setForm,
    setEditMode,
    update,
}: {
    type: string;
    form: NoticeFormProps;
    setForm: React.Dispatch<React.SetStateAction<NoticeFormProps>>;
    setEditMode?: React.Dispatch<React.SetStateAction<boolean>>;
    update?: boolean;
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [fileState, setFileState] = useState<File[]>([]);
    const { mutate, isPending } = usePostConsoleNotice();
    const { mutate: updateNotice, isPending: isPendingUpdate } = usePutConsoleNotice();
    // console.log("form", form);

    // 첨부파일 tooltip
    const filesName = fileState.map(file => `\n${file.name}`);
    const inputTooltipDescription =
        update && fileState.length === 0
            ? form.b_file
            : fileState.length > 0
              ? `선택된 파일 ${fileState.length}개, ${filesName.toString()}`
              : "선택된 파일 없음";

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();

        const isFormEmpty = Object.entries(form).some(([key, value]) => {
            if (excludedKeys.includes(key)) return false;

            if (typeof value === "string") {
                // contents 같은 HTML 입력값의 경우 비어 있는지 검사
                // if (key === "contents") return isEditorEmpty(value);
                return value.trim() === "";
            }

            return !value;
        });

        if (isFormEmpty) {
            let message = "입력 필드를 모두 채워해주세요.";
            if (form.subject.trim() === "") {
                message = "제목을 입력해주세요.";
            }
            toast.error(message, toastErrorSyle);
            return;
        }

        if (update && form.list_no) {
            const data = {
                category: form.category,
                list_no: form.list_no!.toString(),
                name: form.name,
                subject: form.subject,
                contents: form.contents,
                notice: form.notice,
                b_file: fileState,
            };
            updateNotice(data, {
                onSuccess: (data: any) => {
                    toast.success("성공적으로 수정 되었습니다.");
                    setEditMode && setEditMode(false);
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            });
            return;
        }
        mutate(
            { ...form, b_file: fileState },
            {
                onSuccess: (data: any) => {
                    toast.success("성공적으로 게시글 작성 되었습니다.");
                    navigate(`/${type}/manage/notices/${data.data.list_no}`, { state: { page: location.state.page ?? "1" } });
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            },
        );
    };

    return (
        <>
            {isPending || isPendingUpdate ? (
                <LoadingPop />
            ) : (
                <form onSubmit={onSubmit}>
                    <div className="mt-[8px] mb-[40px]">
                        <ConsoleHrozontalHeadTable
                            thead={[
                                {
                                    title: "작성자",
                                    key: "name",
                                    content: (row: string) => (
                                        <div>
                                            <input
                                                type="text"
                                                value={form.name}
                                                disabled
                                                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                                className="border rounded-[5px] px-2 py-1 w-full"
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    title: "제목",
                                    key: "subject",
                                    tdClassName: twMerge(theadStyle, "text-[#666666] text-left"),
                                    content: (row: string) => (
                                        <div>
                                            <input
                                                type="text"
                                                value={form.subject}
                                                onChange={e => setForm(prev => ({ ...prev, subject: e.target.value }))}
                                                className="border rounded-[5px] px-2 py-1 w-full"
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    title: "첨부파일",
                                    key: "b_file",
                                    tdClassName: twMerge(theadStyle, "text-[#666666]"),
                                    content: (row: string) => (
                                        <div className="w-full flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => fileRef.current?.click()}
                                                className="border border-black rounded-[5px] px-3 py-1"
                                            >
                                                파일선택
                                            </button>
                                            <p>{inputTooltipDescription}</p>
                                            <input
                                                type="file"
                                                name="b_file"
                                                ref={fileRef}
                                                // accept={allowedMimeTypes.toString()}
                                                title={inputTooltipDescription}
                                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                    const { files } = event.target;

                                                    if (files && files.length > 0) {
                                                        // 유효한 파일인지 확인
                                                        const invalidFiles = Array.from(files).filter(
                                                            file => !allowedMimeTypes.includes(file.type),
                                                        );
                                                        if (invalidFiles.length > 0) {
                                                            alert("등록 할 수 없는 파일 형식 입니다.");
                                                            return;
                                                        }

                                                        setFileState(Array(files[0]));
                                                    }
                                                    event.target.value = ""; //input value 초기화
                                                }}
                                                className="absolute opacity-0"
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    title: "상단고정",
                                    key: "notice",
                                    tdClassName: twMerge(theadStyle, "text-[#666666]"),
                                    content: (row: string) => (
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="notice"
                                                checked={form.notice ? true : false}
                                                onChange={e => setForm(prev => ({ ...prev, notice: e.target.checked ? 1 : 0 }))}
                                                className="border"
                                            />
                                            <label htmlFor="notice">상단고정 등록시 체크해주세요.</label>
                                        </div>
                                    ),
                                },
                            ]}
                            tbody={form}
                        />
                        <div id="bounds">
                            <Editor
                                bounds="#bounds"
                                value={form.contents}
                                onChangeHandler={(val: string) => setForm(prev => ({ ...prev, contents: val }))}
                                className="[&_.ql-editor]:min-h-[350px] h-auto responsive650:[&_.ql-editor]:min-h-[150px]"
                            />
                        </div>

                        <div className="flex justify-center gap-2 mt-5">
                            <button className="bg-black text-white rounded-[5px] px-4 py-2">저장</button>
                            <button
                                type="button"
                                className="bg-black text-white rounded-[5px] px-4 py-2"
                                onClick={() => (update && setEditMode ? setEditMode(false) : navigate(-1))}
                            >
                                {update ? "취소" : "목록"}
                            </button>
                        </div>
                    </div>
                </form>
            )}
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
